/**
 * EquityFormTable 逻辑层 → ProTableListForm subArray 权益列的翻译器（方案二第二消费者）。
 *
 * 调用 EquityFormTable/equityColumnRules 的 computeEquityColConfig，把「content 列显隐 +
 * actual_content 列标题/必填」规则翻译成 SubArraySpec 的 columns（SubColumnMeta[]），
 * 供 CooperationTableList 的 necessary_rights/expect_rights 子数组渲染 —— 一份规则、两处渲染。
 *
 * 对齐 EquityFormTable/index.tsx 真实行为：
 * - content 列（权益库标准权益）来自「关联权益」弹窗选择结果，只读展示，非可编辑 select
 *   （EquityFormTable 本体也没有 content 列的编辑控件，仅 actual_content 可编辑，见 index.tsx:104-127）；
 * - "添加"改为触发 onRequestAdd（由 CooperationTableList 桥接 useAddEquityModal 打开「关联权益」弹窗），
 *   而非直接 push 空行，见 SubArraySpec.onAddRequest；
 * - necessary_rights 在 sortable 场景（inner && !view）下可拖拽重排，见 SubArraySpec.sortable。
 */
import React from 'react';
import { Input } from '@bedrock/components';
import type { SubColumnMeta, SubArraySpec } from '@mpc/common/src/components/ProTableListForm';
import { computeEquityColConfig, type SType } from '../EquityFormTable/equityColumnRules';
import { ApplyToWholeOrderAction } from './ApplyToWholeOrderAction';
import type { CoverMode } from './applyToWholeOrder';

/** buildEquitySubArraySpec 入参：来自 CooperationTableList 的契约（demo 用 mock） */
export interface EquitySubArraySpecParams {
  /** 主行字段 key，同时是 subArray.key（'necessary_rights' | 'expect_rights'） */
  subKey: 'necessary_rights' | 'expect_rights';
  /** 组标题，如 '必要权益'/'期望权益' */
  title: string;
  /** 权益库标准权益选项来源（DemandRightsContentVo[]），暂保留供 onRequestAdd 打开弹窗时透传 */
  rightsList?: { content?: string; code?: string }[];
  view: boolean;
  inPlanning?: boolean;
  depth?: number;
  $sType: string;
  /** 子项写回（引擎 patchItem 的容器内回调，由 CooperationTableList 注入，暂未消费，占位对齐 kpiSubArraySpec） */
  onItemChange?: (childId: string, patch: Record<string, any>) => void;
  /**
   * "添加{emptyLabel}"点击请求（由 CooperationTableList 注入，桥接 useAddEquityModal 打开「关联权益」弹窗）。
   * 未提供时引擎回退为默认 push(makeDefault()) 行为。
   */
  onRequestAdd?: (row: any, commit: (items: any[]) => void) => void;
  /**
   * 【应用至整单】提供时在该权益组渲染子列动作按钮（见 `SubArraySpec.renderRowAction`）。
   * 入参：该按钮所在**行的行数据** + 用户在弹层里选的 `coverMode`
   * （`retain` 保留原值 / `overwrite` 覆盖原值），由 `CooperationTableList` 承接写回。
   * 不提供则不渲染按钮（如策划拆单预览态）。
   */
  onApplyToTheWholeOrder?: (row: any, coverMode: CoverMode) => void;
}

/** 权益列宽（对齐 EquityFormTable/index.tsx:105,111 的 content / actual_content 列宽） */
const EQUITY_CONTENT_COLUMN_WIDTH = 320;

/** 将 computeEquityColConfig 的 content 列显隐规则翻译成 SubColumnMeta[] */
function buildColumns(params: EquitySubArraySpecParams): SubColumnMeta<any>[] {
  const contentConfig = computeEquityColConfig('content', {
    inPlanning: params.inPlanning,
    view: params.view,
    depth: params.depth,
    $sType: params.$sType as SType,
  });

  const columns: SubColumnMeta<any>[] = [];
  if (!contentConfig.hideInTable) {
    // 权益库标准权益：来自「关联权益」弹窗选择结果，只读展示（对齐 EquityFormTable 本体无编辑控件的 content 列）
    columns.push({
      key: 'content',
      field: 'content',
      title: '权益库标准权益',
      width: EQUITY_CONTENT_COLUMN_WIDTH,
      type: 'custom',
      render: (item: any) => item?.content || '',
    });
  }
  columns.push({
    key: 'actual_content',
    field: 'actual_content',
    // 与 EquityFormTable/index.tsx:154 一致：content 列隐藏时标题为「权益内容」，否则「本单实际权益内容」
    title: contentConfig.hideInTable ? '权益内容' : '本单实际权益内容',
    width: EQUITY_CONTENT_COLUMN_WIDTH,
    type: 'custom',
    required: !params.view,
    render: (item: any, patch: (p: Record<string, unknown>) => void) => {
      // 查看态只读展示；编辑态 input，maxLength 对齐原版 EquityFormTable actual_content（:124 maxLength:200）
      if (params.view) return item?.actual_content || '';
      return (
        <Input
          size="small"
          value={item?.actual_content ?? ''}
          maxLength={200}
          placeholder="请输入"
          style={{ width: '100%' }}
          onChange={(e) => patch({ actual_content: e.target.value })}
        />
      );
    },
  });
  return columns;
}

/** 构建 necessary_rights/expect_rights 的 SubArraySpec（参数化，CooperationTableList useMemo 每次用当前行参数调用） */
export function buildEquitySubArraySpec(params: EquitySubArraySpecParams): SubArraySpec<any, any> {
  const { subKey, title, view, onRequestAdd, onApplyToTheWholeOrder } = params;
  return {
    key: subKey,
    title,
    emptyLabel: '权益',
    // 整体必填（对齐面板模式 CFormList：必要权益 SFormItem required:true，期望权益
    // 无 required 且 tableRequired={false}）→ 组标题星号 + 引擎空子项校验（滚动到「+添加权益」）
    required: subKey === 'necessary_rights',
    // ⚠️ 同 kpiSubArraySpec：真实数据子项可能缺 id 且缺 index_id，必须基于行 id + 下标兜底唯一 key
    getItems: (row) => (row[subKey] || []).map((it: any, index: number) => ({ ...it, id: it.id ?? `${row.id}-${subKey}-${it.index_id ?? index}` })),
    setItems: (row, items) => ({ ...row, [subKey]: items }),
    makeDefault: () => ({ id: `${subKey}-${Date.now()}`, content: '', actual_content: '' }),
    columns: buildColumns(params),
    idPrefix: subKey,
    onAddRequest: onRequestAdd,
    // 查看态无「+添加权益」——对齐面板 EquityFormTable/index.tsx:182 `renderBottom = view ? null : (...)`
    // （权益的添加走「关联权益」弹窗，查看态无添加入口）。引擎据此同步扣减行高。
    disableAdd: !!view,
    // 对齐 EquityFormTable index.tsx:56 的 sortable 常量：仅 inner 且非 view 场景开启。
    // 两张权益表（必要/期望）都可拖拽重排——面板的 sortable 是表级常量，不区分子表。
    sortable: params.$sType === 'inner' && !view,
    // 拖拽跟随预览（对齐 tab 场景「整行 helper 跟随」效果）：鼠标跟随显示被拖权益的完整信息
    // 而非只有把手图标，便于辨认正在拖动的条目；content 为空时回退 actual_content，再空则占位文案
    getDragLabel: (item) => `${title}：${item?.content || item?.actual_content || '（未填写）'}`,
    // 「应用至整单」子列动作（对齐卡片模式 EquityFormTable 的同名按钮）：以本行该权益数组为源
    // 写入整单。显隐与「+添加权益」同门控（`disableAdd = !!view`）。
    // 弹层里选「保留原值 / 覆盖原值」，确认后带回 coverMode；区块名直接复用组标题 `title`
    //（本 spec 由必要权益 / 期望权益参数化共用，故两处弹层与无障碍名各自正确）。
    renderRowAction: onApplyToTheWholeOrder
      ? (row) => (
          <ApplyToWholeOrderAction targetName={title} onApply={(coverMode) => onApplyToTheWholeOrder(row, coverMode)} />
        )
      : undefined,
    // 落点 = **第一子列**（必要权益/期望权益的「权益库标准权益」列）→ 与「+添加权益」
    // 同 cell、同一行并排（开发负责人反馈：权益的整单动作应与添加按钮并排，不另占一格）。
    // ⚠️ 与考核指标（第二子列）**刻意不同**，改这里前先看 dev.spec 陷阱 20。
    rowActionColumn: 'first',
  };
}
